// TODO split into multiple files
import { addDays, formatDistanceToNowStrict } from "date-fns"
import replace from "lodash/replace"
import Big from "big.js"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import pluralize from "pluralize"

import COPY from "~config/copy-constants"
import type {
    AddressFieldsFragment,
    CityAndStateFieldsFragment,
    ListingFieldsFragment,
    PricingPackageFieldsFragment,
} from "~graphql/generated/graphql"
import {
    ListingType,
    PriceByGuest,
    PriceByDay,
} from "~graphql/generated/graphql"
import { LISTING_IMAGE_PLACEHOLDER } from "~config/constants"
import { version } from "~package"

// TODO move away from impoting package
export function getVersionNumber() {
    return version
}

export function formatPrice(
    price: number | null | undefined,
    hideZeroCents: boolean = true
) {
    if (typeof price !== "number") return "N/A"

    const formattedPrice = `$${centsToDollars(price).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
    return hideZeroCents ? replace(formattedPrice, ".00", "") : formattedPrice
}

export function formatPriceWithCents(
    price: number,
    hideZeroCents: boolean = true
) {
    const formattedPrice = `$${centsToDollars(price).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
    return hideZeroCents ? replace(formattedPrice, ".00", "") : formattedPrice
}

export function centsToDollars(cents: number): number {
    return divide(cents, 100)
}

export function dollarsToCents(dollars: number | string): number {
    const cleanedDollars = cleanNumStr(dollars.toString())
    const cents = multiply(cleanedDollars, 100)

    return Math.round(cents)
}

// TODO: move to shared helpers
function cleanNumStr(numStr: string): number {
    const num = Number(numStr.replace(/[^0-9.-]+/g, ""))
    if (isNaN(num)) return 0
    return num
}

export function add(num1: number, num2: number): number {
    return Big(num1).add(num2).toNumber()
}

function multiply(num1: number, num2: number): number {
    return Big(num1).mul(num2).toNumber()
}

function divide(num1: number, num2: number): number {
    return Big(num1).div(num2).toNumber()
}

export function takePercentage(total: number, percent: number): number {
    return Big(total).mul(percent).div(100).toNumber()
}

export function validatePhoneNumber(phone: string): boolean {
    const hasCountryCode = phone.startsWith("+")

    return hasCountryCode && isPossiblePhoneNumber(phone)
}

export function getListingTypeCamelcase(
    type: ListingType | null | undefined = ListingType.Hunting
) {
    switch (type) {
        case ListingType.Hunting:
            return COPY.HUNTING
        case ListingType.Fishing:
            return COPY.FISHING
        case ListingType.BlastCast:
            return COPY.CAST_AND_BLAST
        case ListingType.LodgingOnly:
            return COPY.LODGING
        default:
            return COPY.OTHER
    }
}

export function getCleanEnum(dirty: string): string {
    return dirty
        ? dirty
              .replace(/_/g, " ")
              .split(" ")
              .map((str) => str.charAt(0) + str.slice(1).toLowerCase())
              .join(" ")
        : ""
}

export function getShortLocation(location?: CityAndStateFieldsFragment | null) {
    if (!location) return null

    return `${location.city}, ${location.state}`
}

export function getFullLocation(location?: AddressFieldsFragment | null) {
    if (!location) return null

    return `${location.street_a}, ${
        location.street_b ? `${location.street_b}, ` : ""
    }${location.city}, ${location.state}, ${location.zip}, ${location.country}`
}

// Cleaner string logic, if string exists, return it with optional pretext and posttext
export const stringIfExists = (
    conditionalString: string | null | undefined,
    options?: { pretext?: string; posttext?: string }
): string => {
    const { pretext, posttext } = options || {}

    return conditionalString
        ? `${pretext || ""}${conditionalString}${posttext || ""}`
        : ""
}

export const getUTCNoon = (date: Date | number): Date => {
    const tempDate: Date = new Date(date)

    return new Date(
        Date.UTC(
            tempDate.getFullYear(),
            tempDate.getMonth(),
            tempDate.getDate(),
            12,
            0,
            0
        )
    )
}

// Not using user fragment to allow partial user objects.
export function getFullName(
    user:
        | { first_name?: string | null; last_name?: string | null }
        | null
        | undefined
) {
    if (!user?.first_name || !user.last_name) return ""

    return `${user.first_name} ${user.last_name}`
}

export function getPricingPackageEndDate(
    startDate: Date,
    daysIncluded: number
): Date {
    return addDays(startDate, daysIncluded - 1)
}

export function getArrayOfCount(length: number): number[] {
    return Array.from({ length })
        .fill(null)
        .map((_, index) => index)
}

export function isServerSide() {
    return typeof window === "undefined"
}

export function scrollToElementWithId(
    id: string,
    enableSmothScroll = false,
    position: ScrollLogicalPosition = "start"
) {
    const element = document.getElementById(id)
    if (element) {
        // Smooth scroll causes performance issues.
        element.scrollIntoView({
            behavior: enableSmothScroll ? "smooth" : "auto",
            block: position,
        })
    }
}

export function isString(value: unknown): value is string {
    return typeof value === "string"
}

export function isSetGuestPricingPackage(
    pricingPackage: PricingPackageFieldsFragment | null | undefined
) {
    return pricingPackage?.price_by_guest === PriceByGuest.SetGuest
}

export function isSetDayPricingPackage(
    pricingPackage: PricingPackageFieldsFragment | null | undefined
) {
    return pricingPackage?.price_by_day === PriceByDay.SetDay
}

export function getFirstPhotoUrl(photos: { url: string }[] | null | undefined) {
    return photos?.[0]?.url ?? LISTING_IMAGE_PLACEHOLDER
}

export async function getAndOpenPdfInNewTab({
    getPdfFunc,
}: {
    getPdfFunc: () => Promise<string | null>
}) {
    const pdf = await getPdfFunc()

    if (!pdf) return

    openPdfInNewTab(pdf)
}

function openPdfInNewTab(base64: string) {
    const buffer = Buffer.from(base64, "base64")
    const blob = new Blob([buffer], { type: "application/pdf" })
    const objectUrl = URL.createObjectURL(blob)

    // There's another method using window.open, but we need to use
    // the anchor element trick instead to avoid safari detecting this
    // as a popup and blocking it.
    const anchorElement = document.createElement("a")
    anchorElement.href = objectUrl
    anchorElement.target = "_blank"
    anchorElement.click()
}

// TODO move to date helpers
// returns a string like 1h or 10m instead of 1 hour or 10 minutes
export function getShortDistanceToNow(date: Date) {
    const distanceString = formatDistanceToNowStrict(date)

    // date fns returns a string with amount and units like "1 hour" or "10 minutes"
    const [amount, distance] = distanceString.split(" ")

    // we want to shorted the distance string to just the first letter or mo for month
    const lowerCaseDist = distance?.toLowerCase()
    const shortDistance = lowerCaseDist === "months" ? "mo" : lowerCaseDist

    // result will turn date fns return of "1 hour" into "1h"
    return `${amount}${shortDistance}`
}

export function getCurrentPath() {
    if (isServerSide()) return ""

    return window.location.pathname
}

export function getListingsCountLabel(count: number) {
    return count > 0
        ? `${count} ${pluralize(COPY.LISTING, count)}`
        : "No listings"
}

export function getFirstAvailableDate(listing?: ListingFieldsFragment | null) {
    // if the listing has a next availability date, use that as the first available date
    // otherwise, fallback to the current month
    return new Date(listing?.next_availability?.date || new Date())
}

export function doesListingHaveAvailability<TListing extends object>(
    listing: TListing | null | undefined
) {
    if (!listing) return false

    // if the listing does not have next_availability field defined then we don't know if it has availability or not
    // so we need to assume it does, IE: gatsby generated queries, etc
    if (!("is_unavailable" in listing)) {
        return true
    }

    // if the listing has next_availability field defined, then we can check if it has availability
    return !listing.is_unavailable
}

export function isNil(value: unknown): value is null | undefined {
    return value === null || value === undefined
}

export function isUndefined(value: unknown): value is undefined {
    return value === undefined
}
